import axios from 'axios';
import type { Role } from '../types';
import { isNotInPlan } from '../lib/planGate';

const SKPLATFORM_URL = process.env.SKPLATFORM_API_URL || 'http://localhost:3003';

const skplatform = axios.create({ baseURL: SKPLATFORM_URL, withCredentials: true });

export interface AuthResult {
  token: string;
  role: Role;
  profileId: string;
  email: string;
  firstName: string;
  displayName: string;
  orgId: string;
  orgName: string;
}

export interface SignupInput {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  business_name: string;
  country: string;
  country_code: string;
  phone_number: string;
}

export async function loginWithPassword(email: string, password: string): Promise<AuthResult> {
  const { data } = await skplatform.post<{ data: { access_token: string } }>(
    '/v1/auth/login',
    { email, password },
  );
  const jwt = data.data.access_token;
  return tokenToAuthResult(jwt);
}

export type SignupResult =
  | { verification_required: true; email: string }
  | ({ verification_required: false } & AuthResult);

export async function signupWorkspace(input: SignupInput): Promise<SignupResult> {
  const { data } = await skplatform.post<{
    data: { verification_required: boolean; user: { email: string } };
  }>('/v1/auth/signup', { ...input, product: 'people' });

  if (data.data.verification_required) {
    return { verification_required: true, email: data.data.user.email };
  }
  // Edge case: org already verified (e.g. SSO path) — log in immediately.
  const authResult = await loginWithPassword(input.email, input.password);
  return { verification_required: false, ...authResult };
}

export async function verifyEmail(token: string): Promise<AuthResult | null> {
  const { data } = await skplatform.post<{ data: { access_token?: string } }>(
    '/v1/auth/verify-email',
    { token },
  );
  return data.data.access_token ? tokenToAuthResult(data.data.access_token) : null;
}

export async function resendVerification(): Promise<void> {
  await skplatform.post('/v1/auth/resend-verification', { product: 'people' });
}

export type InvitePlatformRole = 'org_admin' | 'admin' | 'user';

export interface PlatformInvitation {
  id: string;
  org_id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';
  expires_at: string;
  invitee_user_id: string | null;
}

// A missing/empty/sentinel token must NOT become an `Authorization: Bearer `
// header — the backend only falls back to the session cookie when the header
// is absent entirely; an empty Bearer value short-circuits that fallback and
// always 401s. Omit the header so the cookie session can authenticate instead.
function authHeaders(token?: string): { Authorization: string } | undefined {
  return token && token !== 'session' ? { Authorization: `Bearer ${token}` } : undefined;
}

export async function sendInvitation(orgId: string, email: string, role: InvitePlatformRole, token?: string): Promise<void> {
  await skplatform.post(
    `/v1/orgs/${orgId}/invitations`,
    { email, role, products: ['people'] },
    { headers: authHeaders(token) },
  );
}

export async function listInvitations(orgId: string, token?: string): Promise<PlatformInvitation[]> {
  const { data } = await skplatform.get<{ data: PlatformInvitation[] }>(
    `/v1/orgs/${orgId}/invitations`,
    { params: { status: 'pending' }, headers: authHeaders(token) },
  );
  return data.data;
}

export async function resendInvitation(orgId: string, invitationId: string, token?: string): Promise<void> {
  await skplatform.post(
    `/v1/orgs/${orgId}/invitations/${invitationId}/resend`,
    {},
    { headers: authHeaders(token) },
  );
}

export async function cancelInvitation(orgId: string, invitationId: string, token?: string): Promise<void> {
  await skplatform.delete(
    `/v1/orgs/${orgId}/invitations/${invitationId}`,
    { headers: authHeaders(token) },
  );
}

export interface AcceptInvitationInput {
  token: string;
  password?: string;
  first_name?: string;
  last_name?: string;
}

export async function acceptInvitation(input: AcceptInvitationInput): Promise<AuthResult | null> {
  const { data } = await skplatform.post<{ data: { tokens?: { access_token: string } } }>(
    '/v1/auth/accept-invitation',
    input,
  );
  return data.data.tokens?.access_token ? tokenToAuthResult(data.data.tokens.access_token) : null;
}

export async function requestMagicLink(email: string): Promise<void> {
  await skplatform.post('/v1/auth/magic-link', { email, product: 'people' });
}

export async function platformLogout(): Promise<void> {
  try { await skplatform.post('/v1/auth/logout'); } catch { /* ignore — clear local state regardless */ }
}

export async function verifyMagicLink(token: string): Promise<AuthResult> {
  const { data } = await skplatform.post<{ data: { access_token: string } }>(
    '/v1/auth/magic-link/verify',
    { token },
  );
  return tokenToAuthResult(data.data.access_token);
}

function tokenToAuthResult(jwt: string): AuthResult {
  const payload = parseJwtPayload(jwt);
  const platformRoles: string[] = Array.isArray(payload.roles) ? payload.roles : [];
  const fullName = (payload.name as string) ?? '';
  return {
    token: jwt,
    role: deriveRole(platformRoles),
    profileId: (payload.user_id as string) ?? (payload.sub as string) ?? '',
    email: (payload.email as string) ?? '',
    firstName: fullName.split(' ')[0] ?? '',
    displayName: fullName,
    orgId: (payload.platform_org_id as string) ?? '',
    orgName: (payload.org_name as string) ?? '',
  };
}

/**
 * Probe the platform session cookie.
 *
 * THIS RETURNS THREE OUTCOMES, NOT TWO, AND THE THIRD IS THE WHOLE POINT.
 *
 * RequireAuth (App.web.tsx) is the only caller, and a falsy answer there means
 * logout() plus a hard redirect to the platform. So this function is the single
 * thing standing between a signed-in user and a forced sign-out. It used to
 * `catch { return null }`, which collapsed "the platform says you are signed
 * out" together with "I could not get a usable answer" — and every blip in the
 * second category destroyed a perfectly good session.
 *
 * The blip that actually bit us: /v1/me is served with an ETag and NO
 * Cache-Control, so the browser revalidates it and the platform answers 304.
 * axios rejects a 304 — its default validateStatus is 200-299 — the catch fired,
 * and the user was bounced back to login the instant they arrived. Verified
 * against axios 1.18: a 304 rejects with "Request failed with status code 304".
 *
 * Two defences, because either alone still leaves a hole:
 *   1. a unique query parameter, so the browser holds no validator for this URL
 *      and there is nothing for the platform to answer 304 to, and
 *   2. if a 304 arrives anyway, read it instead of throwing — and since a 304
 *      carries no body, re-ask once for one.
 * Only a real 401/403 now means "signed out". Everything else is reported as
 * unavailable, and the caller keeps the session.
 */
export type SessionProbe =
  | { status: 'authenticated'; auth: AuthResult }
  | { status: 'unauthenticated' }
  // Signed in, but the org did not buy the People website (lib/planGate).
  | { status: 'not_in_plan' }
  | { status: 'unavailable'; reason: string };

interface MePayload {
  user?: { id?: string; user_id?: string; email?: string; first_name?: string; last_name?: string };
  org?: { id?: string; name?: string };
  memberships?: { role?: string }[];
  /** The product websites the org bought; null = no restriction. */
  subscribed_products?: string[] | null;
}

// NO CUSTOM HEADERS HERE. `Cache-Control` is not a CORS-safelisted request
// header, so sending it turns this into a preflighted request — and platform's
// Access-Control-Allow-Headers does not list it, so the preflight is refused and
// the GET never happens ("Request header field cache-control is not allowed").
// That is a worse failure than the one it was meant to fix.
//
// A unique query parameter does the same job from the safe side of the fence: a
// URL the cache has never seen has no stored validator, so the browser sends no
// If-None-Match and the platform has nothing to answer 304 to. Identity is
// per-user and credentialed and has no business being cached anyway, so losing
// caching on this one probe costs nothing.
const ME_REQUEST = {
  // Belt and braces: if a 304 ever does arrive, read it rather than throw.
  validateStatus: (s: number) => (s >= 200 && s < 300) || s === 304,
};

function meToAuthResult(d: MePayload): AuthResult {
  const platformRoles: string[] = (d.memberships ?? [])
    .map((m) => m.role)
    .filter((r): r is string => typeof r === 'string');
  const fullName = [d.user?.first_name, d.user?.last_name].filter(Boolean).join(' ');
  return {
    token: 'session',
    role: deriveRole(platformRoles),
    profileId: d.user?.id ?? d.user?.user_id ?? '',
    email: d.user?.email ?? '',
    firstName: d.user?.first_name ?? '',
    displayName: fullName,
    // org identity is platform-owned; read it here so it survives the
    // sentinel 'session' token, which has no org_name claim to decode.
    orgId: d.org?.id ?? '',
    orgName: d.org?.name ?? '',
  };
}

export async function checkSession(): Promise<SessionProbe> {
  try {
    let res = await skplatform.get<{ data?: MePayload }>('/v1/me', {
      ...ME_REQUEST,
      params: { _ts: Date.now() },
    });

    // A 304 is a promise that the cached copy is still good — but when the
    // browser cannot hand that copy back (a Vary: Origin miss, an evicted
    // entry) we are left holding a validator and no body. Ask again for one.
    if (res.status === 304 || !res.data?.data) {
      res = await skplatform.get<{ data?: MePayload }>('/v1/me', {
        ...ME_REQUEST,
        params: { _ts: `${Date.now()}-retry` },
      });
    }

    const d = res.data?.data;
    if (!d?.user) {
      // Reachable, authenticated, and yet unreadable. That is a bug somewhere,
      // not a signed-out user — say so instead of silently signing them out.
      return { status: 'unavailable', reason: `/v1/me returned no user (status ${res.status})` };
    }
    if (isNotInPlan(d.subscribed_products)) return { status: 'not_in_plan' };
    return { status: 'authenticated', auth: meToAuthResult(d) };
  } catch (err: any) {
    const status: number | undefined = err?.response?.status;
    if (status === 401 || status === 403) return { status: 'unauthenticated' };
    return {
      status: 'unavailable',
      reason: status ? `/v1/me failed with ${status}` : (err?.message ?? 'network error'),
    };
  }
}

function deriveRole(platformRoles: string[]): Role {
  if (platformRoles.includes('platform_admin') || platformRoles.includes('org_admin')) return 'hr';
  if (platformRoles.includes('admin')) return 'mss';
  return 'ess';
}

function parseJwtPayload(jwt: string): Record<string, unknown> {
  try {
    const [, payloadB64] = jwt.split('.');
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}
